import Autocomplete from '@mui/material/Autocomplete'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IAllocationScopeItem } from 'types/allocationInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import { InputField } from '../InputField'

/** @notExported  */
interface AllocationScopePickerProps {
  /** The element ID. */
  id?: string
  /** The current value. */
  value?: { value: number; label: string; type: string }
  /** The list of scopes. */
  items: IAllocationScopeItem[]
  /** The function to call when the value changes. */
  onChange: (item: { value: number; label: string; type: string }) => void
  /** The label of the picker. */
  label?: string
  /** Whether the picker is disabled. */
  disabled?: boolean
  /** Whether the picker is required. */
  required?: boolean
  /** Whether to inset label. */
  insetLabel?: boolean
}

/**
 * Allocation scope picker component.
 *
 * @param value - the current value.
 * @param onChange - the function to call when the value changes.
 * @param label - the label of the picker.
 * @param disabled - whether the picker is disabled.
 * @param required - whether the picker is required.
 * @returns the allocation scope picker component.
 * @notExported
 */
const AllocationScopePicker: React.FC<AllocationScopePickerProps> = ({
  id,
  value,
  items,
  onChange,
  label,
  disabled = false,
  required = false,
  insetLabel,
}) => {
  const { t, i18n } = useTranslation()
  const labelId = React.useId()

  const getName = (item: IAllocationScopeItem) => {
    const name = chooseDBTranslation(i18n, item).name
    if (name && name.length) {
      return name
    }

    return t('team.noName')
  }

  const options =
    items?.map(val => ({
      value: val,
      label: getName(val),
      type: val.type,
    })) ?? []

  const loading = !items

  const selected = value ? options.find(option => option.value.id === value.value) : undefined

  return (
    <>
      {!insetLabel && label && <CaleoInputLabel id={labelId} label={label} required={required} />}
      <Autocomplete
        id={id}
        loading={loading}
        disabled={disabled}
        style={{ marginTop: label ? '10px' : 0, minWidth: '280px' }}
        value={selected}
        onChange={(_event, newValue) => {
          if (newValue) {
            onChange({ value: newValue.value.id, label: newValue.label, type: newValue.type })
          }
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={options}
        renderOption={(props, option) => {
          if (typeof option.value === 'string') {
            return <li {...props}>{option.label}</li>
          }
          return (
            <li {...props} key={option.value.id}>
              {option.label}
            </li>
          )
        }}
        renderInput={params => (
          <InputField
            ariaLabelledBy={!insetLabel && label ? labelId : undefined}
            slotProps={{
              inputLabel: insetLabel ? { shrink: true } : {},
              htmlInput: params?.inputProps,
              input: params?.InputProps,
            }}
            {...params}
            placeholder={label && !insetLabel ? '' : t('allocation.selectScope')}
            insetLabel={insetLabel}
            label={insetLabel ? label : undefined}
            margin="dense"
            size="small"
            required={required}
          />
        )}
        fullWidth
      />
    </>
  )
}

export default AllocationScopePicker
